import type { SimCommand } from "../protocol/commands";
import type { SimEvent } from "../protocol/events";
import type { RejectionCode } from "../protocol/driver";
import type { GridPos } from "../protocol/snapshot";
import type { CardDef, LiveBuildRule } from "./definitions";
import { NODE_DEFAULTS, resaleRefund } from "./definitions";
import { layPipe, planPipe, validateRecipeIds } from "./cards";
import { totalUnits } from "./composition";
import { clearContamination, contaminateTile } from "./hazards";
import { heldPenalties } from "./hazardCards";
import {
  edgesTouching,
  facilityMitigationOf,
  findNode,
  findNodeAt,
  isTileContaminated,
  mintCardInstance,
  type EngineEdge,
  type EngineNode,
  type EngineState,
  type InstalledHardware,
} from "./state";

/**
 * Phase 1: Workshop Setup (`00-pillars.md` §3, spec 0017). `installHardware`
 * and `dismantleHardware` are Setup's own commands — buying and placing from
 * `setupCatalogue` in ₵, never from a hand card's kW. Mirrors `cards.ts`'s
 * `PlayResult`/`refuse` shape deliberately (same "every check runs before any
 * mutation" discipline, `02-cards-and-economy.md` §2.2's Refused Plays Cost
 * Zero). Nodes are placed independently of Play's `applyHardware`, but pipes
 * go through `cards.ts`'s `planPipe`/`layPipe`, so a pipe bought here and one
 * laid by a card can never disagree about where a pipe may run.
 */

export type SetupResult =
  | { ok: true }
  | { ok: false; reason: string; code: RejectionCode; sourceId: string | null };

function refuse(reason: string, code: RejectionCode, sourceId: string | null = null): Extract<SetupResult, { ok: false }> {
  return { ok: false, reason, code, sourceId };
}

export function installHardware(args: {
  state: EngineState;
  card: CardDef;
  cmd: Extract<SimCommand, { type: "installHardware" }>;
  events: SimEvent[];
  /** Spec 0067: a Play install honours held Clogged Line locks, which means reading the hand. */
  cards: Record<string, CardDef>;
}): SetupResult {
  const { state, card, cmd, events } = args;

  const gate = buildGate(state, "installed");
  if (!gate.ok) return gate;
  const rule = gate.rule;
  const inPlay = state.phase === "play";
  // Facility mitigations are a Setup purchase only (spec 0067 Constraints).
  if (inPlay && card.facility) {
    return refuse("facility mitigations can only be installed during setup", "wrongPhase");
  }
  // Spec 0028: a metaUtility card carrying a `facility` payload is Setup's
  // other kind of install — sold through the same catalogue and ledger as
  // hardware, but occupying no tile (schema already refuses every other
  // metaUtility/directive/attachment/hazard card a spot in the catalogue).
  if (card.category !== "hardware" && !card.facility) {
    return refuse("only hardware can be installed in setup", "noEffect");
  }

  const offer = (state.stage.setupCatalogue ?? []).find((o) => o.cardId === card.id);
  if (!offer) return refuse(`${card.id} is not in this stage's setup catalogue`, "unknownCard");

  // Spec 0068: after the catalogue, before any money is weighed.
  let picked: readonly string[] | null = null;
  if (cmd.recipeIds !== undefined) {
    const checked = validateRecipeIds(state, card.deploysNode, cmd.recipeIds);
    if (!checked.ok) return checked;
    picked = checked.recipeIds;
  }

  const remaining = state.setupBudgetCredits - state.setupSpentCredits;
  if (offer.costCredits > remaining) return refuse("not enough credits", "notEnoughCredits");

  const labourKw = inPlay && rule ? rule.installLabourKw : 0;
  const noPower = checkLabour(state, labourKw);
  if (noPower) return noPower;
  // Setup has no hand, so no held hazard card can lock a tile there.
  const lockedTiles = inPlay ? heldPenalties(state, args.cards).lockedTiles : [];

  if (card.facility) {
    // Mirrors applyAttachment's (cards.ts) duplicate-attachment refusal:
    // same RejectionCode, same "at most one of this kind" shape.
    if (facilityMitigationOf(state, card.facility.kind)) {
      return refuse(`a ${card.facility.kind} mitigation is already installed`, "alreadyConnected");
    }

    const instance = mintCardInstance(state, card.id);
    state.installedFacilityMitigations.push({ cardId: card.id, mitigation: card.facility, spent: false });
    state.setupSpentCredits += offer.costCredits;
    events.push({
      type: "hardware.installed",
      nodeId: null,
      edgeId: null,
      cardId: card.id,
      instanceId: instance.instanceId,
      costCredits: offer.costCredits,
      labourKw: 0,
    });
    return { ok: true };
  }

  if (card.deploysPipe) {
    const planned = planPipe(state, cmd.fromNodeId, cmd.toNodeId, lockedTiles);
    if (!planned.ok) return planned;
    state.energySpentKw += labourKw;
    const instance = mintCardInstance(state, card.id);
    const edgeId = layPipe(state, card.deploysPipe, planned, {
      cardId: card.id,
      instanceId: instance.instanceId,
      costCredits: offer.costCredits,
    });
    state.setupSpentCredits += offer.costCredits;
    events.push({
      type: "hardware.installed",
      nodeId: null,
      edgeId,
      cardId: card.id,
      instanceId: instance.instanceId,
      costCredits: offer.costCredits,
      labourKw,
    });
    return { ok: true };
  }
  if (!card.deploysNode) return refuse("hardware card deploys nothing", "noEffect");
  if (!cmd.at) return refuse("hardware needs a target tile", "needsTarget");

  const at = cmd.at;
  const blocked = checkNodePlacement(state, at, lockedTiles);
  if (blocked) return blocked;

  state.energySpentKw += labourKw;
  const instance = mintCardInstance(state, card.id);
  const nodeId = `n${state.nextNodeSeq++}`;
  state.nodes.push({
    id: nodeId,
    kind: card.deploysNode,
    pos: { ...at },
    rotation: cmd.rotation ?? NODE_DEFAULTS.rotation,
    buffer: [],
    bufferCapacity: card.deploysNodeConfig?.bufferCapacity ?? NODE_DEFAULTS.bufferCapacity,
    heatCounter: 0,
    criticalTemp: card.deploysNodeConfig?.criticalTemp ?? NODE_DEFAULTS.criticalTemp,
    isOverheatWarning: false,
    overheatTicks: 0,
    catalystHealth: 100,
    isDestroyed: false,
    guardCharges: 0,
    attachments: [],
    emits: null,
    // A hardware card never deploys an inputTank (CLAUDE.md schema impact).
    precursor: null,
    sourcingOptions: null,
    supplyRemaining: null,
    // A bought thermalRegulator carries its card's configured ΔT, if any
    // (spec 0022) — same pattern as bufferCapacity/criticalTemp above. No
    // hardware card ever deploys an inputTank, so feedTemperature has
    // nothing to read here.
    deltaTemp: card.deploysNodeConfig?.deltaTemp ?? null,
    feedTemperature: null,
    splitCursor: 0,
    pumpOverride: card.deploysNodeConfig?.pumpOverride ?? 0,
    valveState: null,
    halfSpeedToggle: false,
    maxMultipleThisTurn: null,
    sludgeActiveThisTick: true,
    infestation: null,
    verminActiveThisTick: true,
    recipeIds: picked ?? card.deploysNodeConfig?.recipeIds ?? null,
    installedHardware: { cardId: card.id, instanceId: instance.instanceId, costCredits: offer.costCredits },
  });

  state.setupSpentCredits += offer.costCredits;
  events.push({
    type: "hardware.installed",
    nodeId,
    edgeId: null,
    cardId: card.id,
    instanceId: instance.instanceId,
    costCredits: offer.costCredits,
    labourKw,
  });

  injectBoundCards(state, card, nodeId);

  return { ok: true };
}

/**
 * Spec 0020 (§Schema Impact): a device's `injectsCards` are the operational
 * cards installing it hands the player, each bound to this node so a later
 * dismantle can find them again. Only `installHardware`'s node branch calls
 * this — no pipe card ever carries `injectsCards` (OQ1), so pipes stay
 * structurally exempt rather than needing their own empty-case check.
 *
 * Appended to the TAIL of the draw pile, never shuffled in and never at a
 * random index the way `pollution.ts`'s `pollute()` inserts a hazard card:
 * `state.drawPile` is already fixed by the seed at `createInitialState`
 * (before any Setup command runs), so the front of the pile — what
 * `drawHand` actually deals on the Setup->Draw transition — is untouched by
 * an install regardless of install order or count. That's what keeps
 * criterion 7's "different install order, same turn-1 hand" true.
 *
 * Sorted ascending by cardId so two devices installed in a different order
 * still append their own cards in the same relative order (Constraints:
 * "appended in stable cardId order").
 */
function injectBoundCards(state: EngineState, card: CardDef, nodeId: string): void {
  if (!card.injectsCards) return;

  const entries = Object.entries(card.injectsCards).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  for (const [cardId, count] of entries) {
    for (let i = 0; i < count; i++) {
      state.drawPile.push(mintCardInstance(state, cardId, nodeId));
    }
  }
}

export function dismantleHardware(args: {
  state: EngineState;
  cmd: Extract<SimCommand, { type: "dismantleHardware" }>;
  events: SimEvent[];
  /** Spec 0067: accepted for symmetry with install; dismantling places nothing, so no lock is read. */
  cards?: Record<string, CardDef>;
}): SetupResult {
  const { state, cmd, events } = args;

  const gate = buildGate(state, "dismantled");
  if (!gate.ok) return gate;
  const rule = gate.rule;
  const inPlay = state.phase === "play";
  if ((cmd.nodeId === undefined) === (cmd.edgeId === undefined)) {
    return refuse("dismantle names exactly one machine or one pipe", "needsTarget");
  }

  const labourKw = inPlay && rule ? rule.dismantleLabourKw : 0;
  const spills: Spill[] = [];

  if (cmd.edgeId !== undefined) {
    const edge = state.edges.find((e) => e.id === cmd.edgeId);
    if (!edge) return refuse("no such pipe", "needsTarget", cmd.edgeId);
    if (edge.installedHardware === null) {
      return refuse("this pipe is part of the stage's rig and cannot be dismantled", "notPlayerInstalled", edge.id);
    }
    const noPower = checkLabour(state, labourKw);
    if (noPower) return noPower;

    state.energySpentKw += labourKw;
    state.edges = state.edges.filter((e) => e.id !== edge.id);
    resetValvesRoutedThrough(state, edge.id);
    const lost = loseTransit(state, edge, "dismantle", spills);
    events.push(refund(state, edge.installedHardware, edge.path, null, edge.id, inPlay ? rule : null, labourKw, lost));
    applySpills(state, spills, events);
    return { ok: true };
  }

  const node = findNode(state, cmd.nodeId!);
  if (!node || node.isDestroyed) return refuse("no such hardware", "needsTarget", cmd.nodeId!);
  if (node.installedHardware === null) {
    return refuse("this hardware is part of the stage's rig and cannot be dismantled", "notPlayerInstalled", node.id);
  }
  const noPower = checkLabour(state, labourKw);
  if (noPower) return noPower;

  state.energySpentKw += labourKw;
  const touching = edgesTouching(state, node.id);
  const touchingIds = new Set(touching.map((e) => e.id));
  state.edges = state.edges.filter((e) => !touchingIds.has(e.id));
  state.nodes = state.nodes.filter((n) => n.id !== node.id);

  purgeBoundCards(state, node.id);

  // A bought pipe can end on a bought machine; taking the machine takes the
  // pipe, and 0020's bound cards need to hear about both. Pricing happens for
  // every unit before any spill lands, so one unit's spill never reprices the
  // next as contaminated.
  const playRule = inPlay ? rule : null;
  let nodeLost = 0;
  for (const edge of touching) {
    resetValvesRoutedThrough(state, edge.id);
    const lost = loseTransit(state, edge, "dismantle", spills);
    if (edge.installedHardware !== null) {
      events.push(refund(state, edge.installedHardware, edge.path, null, edge.id, playRule, 0, lost));
    } else {
      // A stage-rig pipe has no event of its own, so its loss is the node's to report.
      nodeLost += lost;
    }
  }
  nodeLost += loseBuffer(state, node, node.pos, "dismantle", spills);
  events.push(refund(state, node.installedHardware, [node.pos], node.id, null, playRule, labourKw, nodeLost));
  applySpills(state, spills, events);

  return { ok: true };
}

/**
 * Spec 0067: the one Play-phase gate every build command shares. Setup is
 * always open; Play opens only for a stage that authors a `liveBuild` rule;
 * Draw, Run and Status never. `rule` is returned for Play and Setup alike so a
 * caller can ignore it in Setup, where every cost but the ₵ price is zero.
 */
function buildGate(
  state: EngineState,
  verb: string,
): { ok: true; rule: LiveBuildRule | null } | Extract<SetupResult, { ok: false }> {
  if (state.phase === "setup") return { ok: true, rule: state.stage.liveBuild ?? null };
  if (state.phase === "play" && state.stage.liveBuild) return { ok: true, rule: state.stage.liveBuild };
  return refuse(`hardware can only be ${verb} during setup, or during play on a stage with live building (currently ${state.phase})`, "wrongPhase");
}

/** kW labour is checked with the other refusals and only charged once they have all passed. */
function checkLabour(state: EngineState, labourKw: number): Extract<SetupResult, { ok: false }> | null {
  if (labourKw > state.energyBudgetKw - state.energySpentKw) return refuse("not enough power", "notEnoughPower");
  return null;
}

/** Placement checks a node install and a node move share, in the same order. */
function checkNodePlacement(
  state: EngineState,
  at: GridPos,
  lockedTiles: readonly GridPos[],
): Extract<SetupResult, { ok: false }> | null {
  if (at.x < 0 || at.x >= state.stage.cols || at.y < 0 || at.y >= state.stage.rows) {
    return refuse("tile is off the board", "targetOffBoard", `${at.x},${at.y}`);
  }
  const occupying = findNodeAt(state, at);
  if (occupying) return refuse("tile is occupied", "targetOccupied", occupying.id);
  if (isTileContaminated(state, at)) return refuse("tile is contaminated", "targetContaminated", `${at.x},${at.y}`);
  if (lockedTiles.some((p) => p.x === at.x && p.y === at.y)) {
    return refuse("tile is locked", "targetLocked", `${at.x},${at.y}`);
  }
  return null;
}

/** A spill a loaded dismantle or move owes: applied only after every unit is priced. */
interface Spill {
  pos: GridPos;
  cause: "dismantle" | "move";
  sourceId: string;
}

/**
 * Empties a pipe's transit packets, counting them as wasted (spec 0067 OQ6),
 * and queues one spill at the head packet's waypoint, as `applyLeakLosses`
 * does. Returns the units lost; an empty pipe never spills.
 */
function loseTransit(state: EngineState, edge: EngineEdge, cause: Spill["cause"], spills: Spill[], spillFree = false): number {
  let head: { pathIndex: number } | null = null;
  let lost = 0;
  for (const t of edge.transit) {
    lost += totalUnits(t.packet.composition);
    if (head === null || t.pathIndex > head.pathIndex) head = t;
  }
  edge.transit = [];
  if (lost === 0 || spillFree) return 0;
  state.wastedUnits += lost;
  spills.push({ pos: { ...edge.path[head!.pathIndex]! }, cause, sourceId: edge.id });
  return lost;
}

/** The node counterpart of `loseTransit`: the spill lands on `tile`, the one the unit stood on. */
function loseBuffer(state: EngineState, node: EngineNode, tile: GridPos, cause: Spill["cause"], spills: Spill[]): number {
  let lost = 0;
  for (const packet of node.buffer) lost += totalUnits(packet.composition);
  node.buffer = [];
  if (lost === 0) return 0;
  state.wastedUnits += lost;
  spills.push({ pos: { ...tile }, cause, sourceId: node.id });
  return lost;
}

function applySpills(state: EngineState, spills: readonly Spill[], events: SimEvent[]): void {
  for (const spill of spills) contaminateTile(state, spill.pos, spill.cause, spill.sourceId, events);
}

/**
 * Setup-phase only (spec 0027): chooses or swaps an `inputTank`'s supply
 * contract. Mirrors `installHardware`'s discipline — every check runs before
 * any mutation — but charges no event: `NodeState.sourcing` on the snapshot
 * is the record, not a narrated purchase (Constraints: "the snapshot is the
 * record").
 */
export function setPrecursor(args: {
  state: EngineState;
  cmd: Extract<SimCommand, { type: "setPrecursor" }>;
  events: SimEvent[];
}): SetupResult {
  const { state, cmd } = args;

  if (state.phase !== "setup") {
    return refuse(`precursor can only be chosen during setup (currently ${state.phase})`, "wrongPhase");
  }

  const node = findNode(state, cmd.nodeId);
  if (!node) return refuse("no such tank", "needsTarget", cmd.nodeId);
  if (node.kind !== "inputTank") return refuse("only an inputTank draws from a precursor contract", "wrongNodeKind", node.id);
  if (node.sourcingOptions === null) return refuse("this tank has no sourcing contract to choose", "noEffect", node.id);

  const options = node.sourcingOptions;
  const costOf = (tier: "budget" | "premium") => (tier === "budget" ? options.budget.costCredits : options.premium.costCredits);
  const currentCost = node.precursor === null ? 0 : costOf(node.precursor.tier);
  const newCost = costOf(cmd.tier);
  const delta = newCost - currentCost;

  if (delta > state.setupBudgetCredits - state.setupSpentCredits) {
    return refuse("not enough credits", "notEnoughCredits", node.id);
  }

  state.setupSpentCredits += delta;

  // A same-tier re-choice is a zero-delta no-op on the ledger, but it must
  // NOT touch supplyRemaining — otherwise clicking the same tier again would
  // silently refill a partially-drained Premium cap. A genuine first choice
  // or tier switch is a fresh contract, so it (re-)charges the full cap.
  if (cmd.tier !== node.precursor?.tier) {
    node.precursor =
      cmd.tier === "budget" ? { tier: "budget", impurities: options.budget.impurities } : { tier: "premium" };
    node.supplyRemaining = cmd.tier === "premium" ? options.premium.supplyCap ?? null : null;
  }

  return { ok: true };
}

/**
 * Spec 0020 (Constraints: "a purge is total"): dismantling a node strikes
 * every instance it injected, wherever it currently sits — draw pile, hand,
 * or already discarded. A bound card surviving its own hardware is the bug
 * this spec exists to prevent, so all three piles are filtered
 * unconditionally rather than only the ones a particular test happens to
 * exercise.
 *
 * Only the node branch of `dismantleHardware` calls this — no pipe ever
 * binds a card (OQ1), so there is nothing for the edge branch to purge.
 */
function purgeBoundCards(state: EngineState, nodeId: string): void {
  const keeps = (ci: { boundNodeId: string | null }) => ci.boundNodeId !== nodeId;
  state.drawPile = state.drawPile.filter(keeps);
  state.hand = state.hand.filter(keeps);
  state.discardPile = state.discardPile.filter(keeps);
}

/**
 * Spec 0024 (Resolved Question 3): dismantling a routed edge leaves its
 * mover's `route` valve naming a pipe that no longer exists, so it resets to
 * `null` — no fallback edge to fall back to. Silent, no event of its own,
 * the same spirit as `purgeBoundCards`: `hardware.dismantled` already
 * narrates the dismantle that caused it.
 */
function resetValvesRoutedThrough(state: EngineState, edgeId: string): void {
  for (const node of state.nodes) {
    if (node.kind === "flowMover" && node.valveState?.kind === "route" && node.valveState.edgeId === edgeId) {
      node.valveState = null;
    }
  }
}

/**
 * Prices one dismantled unit against the stage's `resaleRule` (spec 0021) and
 * moves the setup ledger by exactly the figure the event reports, so the two
 * cannot disagree (criterion 6). A disposal fee is a negative refund: it
 * raises spend.
 *
 * Age is always 0. Setup runs once per stage and nothing on the board
 * outlives it, so every unit dismantled here was bought this same setup — the
 * ladder's 60 % and 25 % rungs are `@reagent/meta`'s, and only bite once
 * hardware carries between stages. Contamination is `03-hazards.md` §3.1's,
 * read at the moment of the dismantle (0021 Open Question 1): a unit standing
 * on any contaminated tile has no resale value and costs a disposal fee.
 * Callers pass the footprint because a node is gone from `state` by the time
 * its refund is priced, but its tiles' contamination is not.
 *
 * Spec 0067: `playRule` is non-null only for a Play dismantle, where a CLEAN
 * unit's ladder refund is cut by `playRefundPct` (a contaminated unit's
 * disposal fee is not — the cut is for resale value, and a fee has none).
 */
function refund(
  state: EngineState,
  installed: InstalledHardware,
  footprint: readonly GridPos[],
  nodeId: string | null,
  edgeId: string | null,
  playRule: LiveBuildRule | null,
  labourKw: number,
  lostUnits: number,
): SimEvent {
  const contaminated = footprint.some((pos) => isTileContaminated(state, pos));
  const ladder = resaleRefund(installed.costCredits, 0, contaminated, state.stage.resaleRule);
  const refundCredits = playRule && !contaminated ? Math.floor((ladder * playRule.playRefundPct) / 100) : ladder;
  state.setupSpentCredits -= refundCredits;
  return {
    type: "hardware.dismantled",
    nodeId,
    edgeId,
    cardId: installed.cardId,
    instanceId: installed.instanceId,
    refundCredits,
    labourKw,
    lostUnits,
  };
}

/**
 * Spec 0067 OQ4 (owner): a pipe on a moved node is sold back at cost — its
 * full purchase price, no Play cut, no depreciation, no labour. A pipe lying
 * on a contaminated tile pays the unchanged disposal fee instead.
 */
function sellBack(state: EngineState, edge: EngineEdge, installed: InstalledHardware, lostUnits: number): SimEvent {
  const contaminated = edge.path.some((pos) => isTileContaminated(state, pos));
  const refundCredits = contaminated
    ? resaleRefund(installed.costCredits, 0, true, state.stage.resaleRule)
    : installed.costCredits;
  state.setupSpentCredits -= refundCredits;
  return {
    type: "hardware.dismantled",
    nodeId: null,
    edgeId: edge.id,
    cardId: installed.cardId,
    instanceId: installed.instanceId,
    refundCredits,
    labourKw: 0,
    lostUnits,
  };
}

function transitUnits(edge: EngineEdge): number {
  let units = 0;
  for (const t of edge.transit) units += totalUnits(t.packet.composition);
  return units;
}

/**
 * Spec 0067: moves a bought machine to another tile, or re-attaches a bought
 * pipe to new endpoints (OQ10). Free in Setup (nothing flows, so nothing can
 * spill); in Play it costs `moveLabourKw` and `moveCostCredits`. Every check
 * runs before the first mutation, in the Constraints' order.
 */
export function moveHardware(args: {
  state: EngineState;
  cmd: Extract<SimCommand, { type: "moveHardware" }>;
  events: SimEvent[];
  cards: Record<string, CardDef>;
}): SetupResult {
  const { state, cmd, events } = args;

  const gate = buildGate(state, "moved");
  if (!gate.ok) return gate;
  const inPlay = state.phase === "play";
  const rule = inPlay ? gate.rule : null;
  if ((cmd.nodeId === undefined) === (cmd.edgeId === undefined)) {
    return refuse("move names exactly one machine or one pipe", "needsTarget");
  }

  const costCredits = rule?.moveCostCredits ?? 0;
  const labourKw = rule?.moveLabourKw ?? 0;
  const lockedTiles = inPlay ? heldPenalties(state, args.cards).lockedTiles : [];
  const spills: Spill[] = [];

  if (cmd.edgeId !== undefined) {
    const edge = state.edges.find((e) => e.id === cmd.edgeId);
    if (!edge) return refuse("no such pipe", "needsTarget", cmd.edgeId);
    if (edge.installedHardware === null) {
      return refuse("this pipe is part of the stage's rig and cannot be moved", "notPlayerInstalled", edge.id);
    }
    // A pipe on a contaminated tile is refused for the same reason a node is
    // (OQ8): move-then-dismantle would dodge the disposal fee.
    const dirty = edge.path.find((p) => isTileContaminated(state, p));
    if (dirty) return refuse("this pipe lies on a contaminated tile", "targetContaminated", `${dirty.x},${dirty.y}`);

    // The pipe's own current route must not count as "already connected".
    const planned = planPipe({ ...state, edges: state.edges.filter((e) => e.id !== edge.id) }, cmd.fromNodeId, cmd.toNodeId, lockedTiles);
    // Endpoint errors are about the target and come before ₵/kW; the two
    // placement refusals come after them, as everywhere else.
    if (!planned.ok && planned.code !== "targetContaminated" && planned.code !== "targetLocked") return planned;
    const broke = checkCosts(state, costCredits, labourKw);
    if (broke) return broke;
    if (!planned.ok) return planned;
    if (
      planned.fromNodeId === edge.fromNodeId &&
      planned.toNodeId === edge.toNodeId &&
      planned.path.length === edge.path.length &&
      planned.path.every((p, i) => p.x === edge.path[i]!.x && p.y === edge.path[i]!.y)
    ) {
      return refuse("the pipe already runs there", "noEffect", edge.id);
    }

    const from = edge.path.map((p) => ({ ...p }));
    const loaded = transitUnits(edge) > 0;
    const spillFree = spendSpillFree(state, loaded, rule);
    state.energySpentKw += labourKw;
    state.setupSpentCredits += costCredits;
    const lost = loseTransit(state, edge, "move", spills, spillFree);
    if (edge.fromNodeId !== planned.fromNodeId) resetValvesRoutedThrough(state, edge.id);
    edge.fromNodeId = planned.fromNodeId;
    edge.toNodeId = planned.toNodeId;
    edge.path = planned.path;
    edge.pressureCounter = 0;
    edge.isOverpressured = false;
    events.push({
      type: "hardware.moved",
      nodeId: null,
      edgeId: edge.id,
      from,
      to: planned.path.map((p) => ({ ...p })),
      labourKw,
      costCredits,
      lostUnits: lost,
      spilledAt: spills[0] ? { ...spills[0].pos } : null,
    });
    applySpills(state, spills, events);
    return { ok: true };
  }

  const node = findNode(state, cmd.nodeId!);
  if (!node || node.isDestroyed) return refuse("no such hardware", "needsTarget", cmd.nodeId!);
  if (node.installedHardware === null) {
    return refuse("this hardware is part of the stage's rig and cannot be moved", "notPlayerInstalled", node.id);
  }
  // OQ8: moving off a contaminated tile would dodge the disposal fee a dismantle pays.
  if (isTileContaminated(state, node.pos)) {
    return refuse("this hardware stands on a contaminated tile", "targetContaminated", `${node.pos.x},${node.pos.y}`);
  }
  if (!cmd.at) return refuse("a move needs a destination tile", "needsTarget");
  const at = cmd.at;
  if (at.x === node.pos.x && at.y === node.pos.y) return refuse("the hardware is already there", "noEffect", node.id);
  const broke = checkCosts(state, costCredits, labourKw);
  if (broke) return broke;
  const blocked = checkNodePlacement(state, at, lockedTiles);
  if (blocked) return blocked;

  const departed = { ...node.pos };
  // Pipes do not follow (OQ4): every pipe on the node is sold back, in the
  // order `state.edges` holds them (creation order = id order).
  const touching = edgesTouching(state, node.id);
  let loadedUnits = node.buffer.reduce((sum, p) => sum + totalUnits(p.composition), 0);
  for (const edge of touching) loadedUnits += transitUnits(edge);
  const spillFree = spendSpillFree(state, loadedUnits > 0, rule);

  state.energySpentKw += labourKw;
  state.setupSpentCredits += costCredits;
  const touchingIds = new Set(touching.map((e) => e.id));
  state.edges = state.edges.filter((e) => !touchingIds.has(e.id));

  // Priced before any spill lands, so one pipe's spill never turns the next
  // one's refund into a disposal fee.
  let lostUnits = 0;
  for (const edge of touching) {
    resetValvesRoutedThrough(state, edge.id);
    const lost = loseTransit(state, edge, "move", spills, spillFree);
    lostUnits += lost;
    if (edge.installedHardware !== null) events.push(sellBack(state, edge, edge.installedHardware, lost));
  }
  // A spill-free move carries the buffer along; otherwise it is lost and
  // contaminates the tile the machine left.
  let nodeLost = 0;
  if (!spillFree) nodeLost = loseBuffer(state, node, departed, "move", spills);
  lostUnits += nodeLost;

  node.pos = { ...at };
  if (cmd.rotation !== undefined) node.rotation = cmd.rotation;

  const spilledAt = nodeLost > 0 ? departed : spills[0] ? { ...spills[0].pos } : null;
  events.push({
    type: "hardware.moved",
    nodeId: node.id,
    edgeId: null,
    from: [departed],
    to: [{ ...at }],
    labourKw,
    costCredits,
    lostUnits,
    spilledAt,
  });
  applySpills(state, spills, events);
  return { ok: true };
}

/**
 * Spec 0068: replaces a recipe-running node's binding, stage rig included.
 * Free in Setup; in Play it pays `installLabourKw` and no ₵. The node keeps its
 * id, cards, buffer and heat (OQ8), so this only swaps the list
 * `recipesForNode` reads from the next tick.
 */
export function bindRecipe(args: {
  state: EngineState;
  cmd: Extract<SimCommand, { type: "bindRecipe" }>;
  events: SimEvent[];
}): SetupResult {
  const { state, cmd, events } = args;

  const gate = buildGate(state, "rebound");
  if (!gate.ok) return gate;
  const labourKw = state.phase === "play" && gate.rule ? gate.rule.installLabourKw : 0;

  const node = findNode(state, cmd.nodeId);
  if (!node) return refuse("no such hardware", "needsTarget", cmd.nodeId);
  if (!state.stage.recipes.some((r) => r.nodeKinds.includes(node.kind))) {
    return refuse(`a ${node.kind} runs no recipes`, "wrongNodeKind", node.id);
  }
  if (node.isDestroyed) return refuse("this hardware is destroyed", "noEffect", node.id);

  const checked = validateRecipeIds(state, node.kind, cmd.recipeIds);
  if (!checked.ok) return checked;
  const next = checked.recipeIds;
  if (node.recipeIds !== null && node.recipeIds.length === next.length && node.recipeIds.every((id, i) => id === next[i])) {
    return refuse("the node is already bound to those recipes", "noEffect", node.id);
  }
  const noPower = checkLabour(state, labourKw);
  if (noPower) return noPower;

  state.energySpentKw += labourKw;
  node.recipeIds = next;
  events.push({ type: "hardware.rebound", nodeId: node.id, recipeIds: [...next], labourKw, costCredits: 0 });
  return { ok: true };
}

/** ₵ then kW, the Constraints' order; neither is charged here. */
function checkCosts(state: EngineState, costCredits: number, labourKw: number): Extract<SetupResult, { ok: false }> | null {
  if (costCredits > state.setupBudgetCredits - state.setupSpentCredits) return refuse("not enough credits", "notEnoughCredits");
  return checkLabour(state, labourKw);
}

/**
 * Criterion 10's hook: a loaded move spends one of the turn's spill-free
 * moves while any remain. An empty move never spends one. Play only — `rule`
 * is null in Setup.
 */
function spendSpillFree(state: EngineState, loaded: boolean, rule: LiveBuildRule | null): boolean {
  if (!loaded || !rule || state.spillFreeMovesUsedThisTurn >= rule.spillFreeMovesPerTurn) return false;
  state.spillFreeMovesUsedThisTurn += 1;
  return true;
}

/**
 * Spec 0067: the ₵ route to cleaning a tile (the kW route is `card-scrub`).
 * Charges `cleanFeeCredits` and no kW, so it works under a zeroed budget. A
 * stage with no `liveBuild` rule has no fee to charge, so it is refused in
 * Setup as in Play.
 */
export function cleanTile(args: {
  state: EngineState;
  cmd: Extract<SimCommand, { type: "cleanTile" }>;
  events: SimEvent[];
}): SetupResult {
  const { state, cmd, events } = args;
  const rule = state.stage.liveBuild;
  if (!rule || (state.phase !== "setup" && state.phase !== "play")) {
    return refuse(`tiles can only be cleaned during setup or play on a stage with live building (currently ${state.phase})`, "wrongPhase");
  }
  const at = cmd.at;
  if (at.x < 0 || at.x >= state.stage.cols || at.y < 0 || at.y >= state.stage.rows) {
    return refuse("tile is off the board", "targetOffBoard", `${at.x},${at.y}`);
  }
  if (!isTileContaminated(state, at)) return refuse("tile is not contaminated", "noEffect", `${at.x},${at.y}`);
  if (rule.cleanFeeCredits > state.setupBudgetCredits - state.setupSpentCredits) {
    return refuse("not enough credits", "notEnoughCredits");
  }

  state.setupSpentCredits += rule.cleanFeeCredits;
  clearContamination(state, at);
  events.push({ type: "tile.cleaned", pos: { ...at }, method: "fee", costCredits: rule.cleanFeeCredits });
  return { ok: true };
}
